import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Globe2, Moon, Sun, Menu, X, Users, Search, UserPlus, Trophy, Gavel, ArrowLeftRight, FileText, MessageSquare, BookOpen as BookOpenIcon, History as HistoryIcon, Settings, Eye, AlertTriangle, BarChart3, Hammer, LayoutList, Shield, Image as ImageIcon, Activity } from 'lucide-react';
import { QuickPlayerSearch } from './QuickPlayerSearch';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { clearLocalUserSession } from '../../lib/tenantSession';

export function Navbar() {
  const navigate = useNavigate();
  const { username } = useUserStore();
  const currentUserId = useUserStore((state) => state.user_id);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { language, setLanguage } = useLanguageStore();
  const { theme, toggleTheme } = useThemeStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasLeagueViolations, setHasLeagueViolations] = useState(false);
  const [isDraftActive, setIsDraftActive] = useState(false);
  const [pendingTrades, setPendingTrades] = useState(0);
  const [pendingJoinRequests, setPendingJoinRequests] = useState(0);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const is_impersonating = useUserStore((state) => state.is_impersonating);
  const stopImpersonation = useUserStore((state) => state.stopImpersonation);
  const isBpmSession = useUserStore((state) => state.is_bpm_session);
  const isTslhSession = useUserStore((state) => state.is_tslh_session);
  // Le menu mobile "restreint" (BPM ou TSLH) partage la même liste de raccourcis
  // essentiels : Accueil / Mon équipe / Intra-ligue / Classement.
  const isRestrictedSession = isBpmSession || isTslhSession;
  const { isStandardFlexible, isBuildPhase } = useLeagueMode();
  const isBuildAlert = isStandardFlexible && isBuildPhase;
  const [nhlTeamAbbrev, setNhlTeamAbbrev] = useState<string | null>(null);

  // Check for league-wide roster violations
  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    const checkLeagueViolations = async () => {
      // Pas de validation d'alignement pour les ligues standard_flexible
      if (isStandardFlexible) {
        setHasLeagueViolations(false);
        return;
      }
      try {
        const { data: settings, error: settingsError } = await supabase
          .from('league_settings')
          .select('number_foward, number_def, number_goalie')
          .eq('league_id', selectedLeague.id)
          .single();

        if (settingsError) {
          console.error('Error fetching league settings:', settingsError);
          return;
        }

        const { data: leagueUsers, error: usersError } = await supabase
          .from('leagues_users')
          .select('user_id')
          .eq('league_id', selectedLeague.id);

        if (usersError) {
          console.error('Error fetching league users:', usersError);
          return;
        }

        let hasViolations = false;

        for (const leagueUser of leagueUsers || []) {
          const { data: playerCounts } = await supabase
            .from('get_number_type_player_per_team')
            .select('number_of_fowards, number_of_defenders')
            .eq('league_id', selectedLeague.id)
            .eq('user_id', leagueUser.user_id)
            .single();

          if (playerCounts) {
            const maxForwards = settings?.number_foward || 9;
            const actualForwards = playerCounts.number_of_fowards || 0;
            if (actualForwards > maxForwards || actualForwards < maxForwards) {
              hasViolations = true;
              break;
            }

            const maxDefenders = settings?.number_def || 6;
            const actualDefenders = playerCounts.number_of_defenders || 0;
            if (actualDefenders > maxDefenders || actualDefenders < maxDefenders) {
              hasViolations = true;
              break;
            }
          }

          const { data: goalieCounts } = await supabase
            .from('get_number_type_goalie_per_team')
            .select('number_of_goalie')
            .eq('league_id', selectedLeague.id)
            .eq('user_id', leagueUser.user_id)
            .single();

          if (goalieCounts) {
            const maxGoalies = settings?.number_goalie || 3;
            const actualGoalies = goalieCounts.number_of_goalie || 0;
            if (actualGoalies > maxGoalies || actualGoalies < maxGoalies) {
              hasViolations = true;
              break;
            }
          }
        }

        setHasLeagueViolations(hasViolations);
      } catch (error) {
        console.error('Error checking league violations:', error);
      }
    };

    checkLeagueViolations();
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) return;

    const checkPendingTrades = async () => {
      const { data } = await supabase
        .from('gettradeshistory_players_and_users')
        .select('th_id')
        .eq('leagueid', selectedLeague.id)
        .eq('receiver_user_id', currentUserId)
        .eq('is_accepted', false)
        .eq('is_refused', false);
      setPendingTrades(data?.length || 0);
    };

    const checkCommissionerAndJoinRequests = async () => {
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('admin_user_id, admin_user_id2')
        .eq('league_id', selectedLeague.id)
        .single();
      const isComm = leagueData
        ? leagueData.admin_user_id === Number(currentUserId) || leagueData.admin_user_id2 === Number(currentUserId)
        : false;
      setIsCommissioner(isComm);
      if (isComm) {
        const { data: invites } = await supabase
          .from('league_invitation')
          .select('*')
          .eq('league_id', selectedLeague.id);
        setPendingJoinRequests(invites?.length || 0);
      } else {
        setPendingJoinRequests(0);
      }
    };

    checkPendingTrades();
    checkCommissionerAndJoinRequests();

    // Charger le logo NHL de l'équipe
    setNhlTeamAbbrev(null);
    supabase
      .from('leagues_users')
      .select('nhl_team_abbrev')
      .eq('league_id', selectedLeague.id)
      .eq('user_id', Number(currentUserId))
      .single()
      .then(({ data }) => {
        if (data) {
          setNhlTeamAbbrev((data as { nhl_team_abbrev?: string | null }).nhl_team_abbrev ?? null);
        }
      });

    // TEMP DEBUG iPhone WebSocket - Désactivation temporaire des subscriptions Navbar
    /*
    const tradesSubscription = supabase
      .channel('trades-changes-navbar')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'trades_history',
        filter: `receiver_user_id=eq.${currentUserId}`,
      }, () => checkPendingTrades())
      .subscribe();

    const joinRequestsSubscription = supabase
      .channel('join-requests-changes-navbar')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'league_invitation',
        filter: `league_id=eq.${selectedLeague.id}`,
      }, () => checkCommissionerAndJoinRequests())
      .subscribe();
    */

    return () => {
      // TEMP DEBUG iPhone WebSocket - Désactivation temporaire du cleanup Navbar
      // tradesSubscription.unsubscribe();
      // joinRequestsSubscription.unsubscribe();
    };
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (!selectedLeague) return;

    let isMounted = true;

    const checkDraftStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('league_settings')
          .select('draftMode')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error && (error as any).code !== 'PGRST116') {
          console.error('Error checking draft status:', error);
          return;
        }

        if (isMounted) {
          setIsDraftActive(!!data?.draftMode);
        }
      } catch (err) {
        console.error('Error checking draft status:', err);
        if (isMounted) {
          setIsDraftActive(false);
        }
      }
    };

    checkDraftStatus();

    // TEMP DEBUG iPhone WebSocket - Désactivation temporaire de la subscription draftMode Navbar
    /*
    const draftStatusSubscription = supabase
      .channel('league-settings-draft-navbar')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'league_settings', filter: `league_id=eq.${selectedLeague.id}` },
        (payload) => {
          const newDraft = (payload as any)?.new?.draftMode;
          if (typeof newDraft === 'boolean') {
            setIsDraftActive(newDraft);
          }
        }
      )
      .subscribe();
    */

    return () => {
      isMounted = false;
      // TEMP DEBUG iPhone WebSocket - Désactivation temporaire du cleanup draftMode Navbar
      // draftStatusSubscription.unsubscribe();
    };
  }, [selectedLeague]);

  const handleLogout = () => {
    clearLocalUserSession();
    toast.success('Logged out successfully');
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const renderBuildMyTeamButton = () => (
    <button
      className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
      onClick={() => {
        setIsMobileMenuOpen(false);
        navigate('/build-my-team');
      }}
      title="Bâtir mon équipe"
      style={{
        background: 'rgba(245, 158, 11, 0.2)',
        color: '#fbbf24',
        textAlign: 'left',
        border: '1px solid rgba(245, 158, 11, 0.5)',
        borderRadius: '12px',
        fontWeight: 700,
      }}
    >
      <Hammer className="me-3" />
      <span className="fw-bold">Bâtir mon équipe</span>
      <span className="badge ms-auto" style={{ backgroundColor: '#FACC15', color: '#1f2937', fontSize: '0.7rem' }}>À faire</span>
    </button>
  );

  const renderDraftButton = () => (
    <button
      className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
      onClick={() => {
        setIsMobileMenuOpen(false);
        navigate('/draft');
      }}
      title="Repêchage"
      style={{ 
        background: isDraftActive ? 'rgba(245, 158, 11, 0.2)' : 'transparent', 
        color: isDraftActive ? '#fbbf24' : 'white', 
        textAlign: 'left',
        border: isDraftActive ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid transparent',
        borderRadius: '12px'
      }}
    >
      <Gavel className="me-3" />
      <span className="fw-semibold">Repêchage de la ligue</span>
    </button>
  );

  return (
    <>
      <nav 
        className="navbar position-fixed w-100 top-0" 
        style={{ 
          zIndex: 1000,
          background: 'var(--navbar-bg)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          padding: '1rem 0',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="container">
          <div className="d-flex justify-content-between align-items-center w-100">
            {/* Logo */}
            <Link 
              className="navbar-brand d-flex align-items-center text-white fw-bold fs-4" 
              to={username ? '/dashboard' : '/'}
              style={{
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div 
                className="me-2 d-flex align-items-center justify-content-center"
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)',
                  overflow: 'hidden',
                }}
              >
                <img src="/image2.png" alt="Hockey Dynasty" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              <span className="d-none d-sm-inline">Hockey Dynasty</span>
              <span className="d-inline d-sm-none">HD</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="d-none d-lg-flex align-items-center">

              <button
                className="btn me-3 d-flex align-items-center px-3 py-2"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderRadius: '50px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {theme === 'dark' ? (
                  <Sun size={16} className="me-2" />
                ) : (
                  <Moon size={16} className="me-2" />
                )}
                <span className="small fw-semibold">{theme === 'dark' ? 'Clair' : 'Sombre'}</span>
              </button>
              
              {/* Quick player search — desktop */}
              <div className="me-3" style={{ width: 220 }}>
                <QuickPlayerSearch />
              </div>

              <button
                className="btn me-3 d-flex align-items-center px-3 py-2"
                onClick={toggleLanguage}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderRadius: '50px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Globe2 size={16} className="me-2" />
                <span className="small fw-semibold">{language === 'en' ? 'FR' : 'EN'}</span>
              </button>
              
              {username ? (
                <>
                  <span className="nav-link text-white fw-semibold me-3">
                    Bienvenue, {username}
                  </span>
                  {is_impersonating && (
                    <button
                      className="btn btn-sm btn-outline-light me-3"
                      onClick={() => {
                        stopImpersonation();
                        toast.success('Vous êtes revenu à votre compte');
                        navigate('/my-office');
                      }}
                      title="Revenir à mon compte"
                    >
                      Revenir à mon compte
                    </button>
                  )}
                  <button 
                    className="btn d-flex align-items-center px-3 py-2"
                    onClick={handleLogout}
                    style={{
                      background: 'linear-gradient(45deg, #ef4444, #dc2626)',
                      border: 'none',
                      color: 'white',
                      borderRadius: '50px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.3)';
                    }}
                  >
                    <LogOut size={16} className="me-2" />
                    <span className="small fw-semibold">Déconnexion</span>
                  </button>
                </>
              ) : (
                <Link 
                  className="btn d-flex align-items-center px-4 py-2"
                  to="/"
                  style={{
                    background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)';
                  }}
                >
                  Connexion
                </Link>
              )}
            </div>

            {/* Mobile Navigation Controls */}
            <div className="d-flex d-lg-none align-items-center">
              {username && (
                <button 
                  className="btn btn-sm me-2 d-flex align-items-center px-2 py-1"
                  onClick={handleLogout}
                  style={{
                    background: 'linear-gradient(45deg, #ef4444, #dc2626)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 10px rgba(239, 68, 68, 0.3)'
                  }}
                >
                  <LogOut size={14} className="me-1" />
                  <span className="small">Sortir</span>
                </button>
              )}
              
              <button
                className="btn d-flex align-items-center p-2"
                onClick={toggleMobileMenu}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Quick player search — mobile (toujours visible sous le header) */}
          <div className="d-lg-none w-100" style={{ marginTop: '0.5rem' }}>
            <QuickPlayerSearch
              onNavigate={() => setIsMobileMenuOpen(false)}
              className="w-100"
            />
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="position-fixed w-100 h-100 d-lg-none"
          style={{ top: 0, left: 0, zIndex: 999, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
          onClick={() => {
            setIsMobileMenuOpen(false);
          }}
        >
          <div
            className="position-absolute start-0 end-0 mx-auto"
            style={{
              top: '72px',
              maxWidth: '520px',
              margin: '0 auto',
              background: 'var(--navbar-bg)',
              borderRadius: '14px',
              padding: '1rem',
              boxShadow: '0 20px 50px rgba(2,6,23,0.6)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <img src="/image2.png" alt="HD" style={{ width: 36, height: 36, borderRadius: 8, marginRight: 12 }} />
                {username && <div className="text-white fw-semibold">Bienvenue, {username}</div>}
              </div>
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                }}
                aria-label="Fermer le menu"
              >
                <X />
              </button>
            </div>

            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 6 }}>

              {is_impersonating && (
                <button
                  className="btn w-100 d-flex align-items-center justify-content-center py-3 btn-warning"
                  onClick={() => {
                    stopImpersonation();
                    toast.success('Vous êtes revenu à votre compte');
                    setIsMobileMenuOpen(false);
                    navigate('/my-office');
                  }}
                >
                  Revenir à mon compte
                </button>
              )}

              <button
                className="btn w-100 d-flex align-items-center justify-content-center py-3"
                onClick={() => {
                  toggleTheme();
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderRadius: '15px',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {theme === 'dark' ? (
                  <Sun size={20} className="me-3" />
                ) : (
                  <Moon size={20} className="me-3" />
                )}
                <span className="fw-semibold">{theme === 'dark' ? 'Mode Clair' : 'Mode Sombre'}</span>
              </button>
              
              <button
                className="btn w-100 d-flex align-items-center justify-content-center py-3"
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderRadius: '15px',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Globe2 size={20} className="me-3" />
                <span className="fw-semibold">Langue: {language === 'en' ? 'Français' : 'English'}</span>
              </button>

              {/* --- MyOffice-like quick links for mobile (simplified) --- */}
              {username && (
                <>
                  {isRestrictedSession ? (
                    <>
                      {/* Mode BPM/TSLH : navigation restreinte aux pages essentielles */}
                      {isBuildAlert && renderBuildMyTeamButton()}
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/my-office'); }}
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <FileText className="me-3" />
                        <span className="fw-semibold">Accueil</span>
                      </button>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/my-teams'); }}
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <Users className="me-3" />
                        <span className="fw-semibold">Mon équipe</span>
                      </button>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/other-teams'); }}
                        title="Intra-ligue"
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <Search className="me-3" />
                        <span className="fw-semibold">Intra-ligue : équipes et joueurs</span>
                      </button>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/line-combinations'); }}
                        title="Combinaisons de lignes"
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <LayoutList className="me-3" />
                        <span className="fw-semibold">Lignes LNH</span>
                      </button>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/standings'); }}
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <Trophy className="me-3" />
                        <span className="fw-semibold">Classement</span>
                      </button>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/team-profile'); }}
                        style={{
                          background: !nhlTeamAbbrev ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                          color: 'white',
                          textAlign: 'left',
                          border: !nhlTeamAbbrev ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid transparent',
                          borderRadius: '12px',
                          fontWeight: !nhlTeamAbbrev ? 700 : 400,
                        }}
                      >
                        <ImageIcon className="me-3" />
                        <span className={!nhlTeamAbbrev ? 'fw-bold' : 'fw-semibold'}>Logo de mon équipe</span>
                        {!nhlTeamAbbrev && (
                          <span className="badge rounded-pill ms-auto" style={{ backgroundColor: '#6366f1', color: 'white', fontSize: '0.7rem' }}>Nouveau</span>
                        )}
                      </button>
                      {!isBuildAlert && (
                        <button
                          className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                          onClick={() => { setIsMobileMenuOpen(false); navigate('/build-my-team'); }}
                          style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                        >
                          <Hammer className="me-3" />
                          <span className="fw-semibold">Bâtir mon équipe</span>
                        </button>
                      )}
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/free-agents'); }}
                        style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                      >
                        <UserPlus className="me-3" />
                        <span className="fw-semibold">Agents libres</span>
                      </button>
                    </>
                  ) : (
                    <>
                  {(pendingTrades > 0 || (isCommissioner && pendingJoinRequests > 0)) && (
                    <button
                      className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate('/messages');
                      }}
                      title="Messages"
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: 'white',
                        textAlign: 'left',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '12px',
                        fontWeight: 700,
                      }}
                    >
                      <MessageSquare className="me-3" />
                      <span className="fw-bold">Messages</span>
                      <span
                        className="badge rounded-pill ms-auto"
                        style={{ backgroundColor: '#ef4444', color: 'white', fontSize: '0.75rem', minWidth: '22px' }}
                      >
                        {pendingTrades + (isCommissioner ? pendingJoinRequests : 0)}
                      </span>
                    </button>
                  )}
                  {isBuildAlert && renderBuildMyTeamButton()}
              {isDraftActive && renderDraftButton()}
                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/my-office');
                    }}
                    title="Bureau général"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <FileText className="me-3" />
                    <span className="fw-semibold">Bureau général</span>
                  </button>

                  {/* Logo d'équipe — mis en évidence si aucun logo choisi */}
                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/team-profile');
                    }}
                    title="Logo de mon équipe"
                    style={{
                      background: !nhlTeamAbbrev ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                      color: 'white',
                      textAlign: 'left',
                      border: !nhlTeamAbbrev ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid transparent',
                      borderRadius: '12px',
                      fontWeight: !nhlTeamAbbrev ? 700 : 400,
                    }}
                  >
                    <ImageIcon className="me-3" />
                    <span className={!nhlTeamAbbrev ? 'fw-bold' : 'fw-semibold'}>Logo de mon équipe</span>
                    {!nhlTeamAbbrev && (
                      <span
                        className="badge rounded-pill ms-auto"
                        style={{ backgroundColor: '#6366f1', color: 'white', fontSize: '0.7rem' }}
                      >
                        Nouveau
                      </span>
                    )}
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/standings');
                    }}
                    title="Classement"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Trophy className="me-3" />
                    <span className="fw-semibold">Classement</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/my-teams');
                    }}
                    title="Mon équipe"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Users className="me-3" />
                    <span className="fw-semibold">Mon équipe</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/guide');
                    }}
                    title="Guide utilisateur"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <BookOpenIcon className="me-3" />
                    <span className="fw-semibold">Guide utilisateur</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/dashboard');
                    }}
                    title="Choisir sa ligue"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <BarChart3 className="me-3" />
                    <span className="fw-semibold">Choisir sa ligue</span>
                  </button>

                  {!isStandardFlexible && (
                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/roster-validation');
                    }}
                    title="Validation des alignements"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left', position: 'relative' }}
                  >
                    <AlertTriangle className="me-3" />
                    <span className="fw-semibold">Validation Alignements</span>
                    {hasLeagueViolations && (
                      <span 
                        className="badge rounded-pill ms-auto"
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.5rem',
                          minWidth: '20px',
                        }}
                      >
                        !
                      </span>
                    )}
                  </button>
                  )}

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/my-teams');
                    }}
                    title="Mon équipe"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Users className="me-3" />
                    <span className="fw-semibold">Mon équipe</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/line-combinations');
                    }}
                    title="Combinaisons de lignes"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <LayoutList className="me-3" />
                    <span className="fw-semibold">Lignes NHL</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/nhl-edge-hub');
                    }}
                    title="NHL Edge Stats Hub"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Activity className="me-3" />
                    <span className="fw-semibold">NHL Edge™ Stats</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/advanced-player-search');
                    }}
                    title="Recherche de joueurs"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Search className="me-3" />
                    <span className="fw-semibold">Recherche de joueurs</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/trade-player');
                    }}
                    title="Échanges"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <ArrowLeftRight className="me-3" />
                    <span className="fw-semibold">Échanges</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/other-teams');
                    }}
                    title="Intra-ligue"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Search className="me-3" />
                    <span className="fw-semibold">Intra-ligue : équipes et joueurs</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/trading-bloc');
                    }}
                    title="Trading Bloc"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <FileText className="me-3" />
                    <span className="fw-semibold">Trading Bloc</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/free-agents');
                    }}
                    title="Agents libres"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <UserPlus className="me-3" />
                    <span className="fw-semibold">Agents libres</span>
                  </button>

                  {!isBuildAlert && (
                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/build-my-team');
                    }}
                    title="Bâtir mon équipe"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Hammer className="me-3" />
                    <span className="fw-semibold">Bâtir mon équipe</span>
                  </button>
                  )}

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/league-rules');
                    }}
                    title=" Règles/Infos Pool"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <BookOpenIcon className="me-3" />
                    <span className="fw-semibold"> Règles/Infos Pool</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/watchlist');
                    }}
                    title="Joueurs surveillés"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Eye className="me-3" />
                    <span className="fw-semibold">Joueurs surveillés</span>
                  </button>

                  {!isDraftActive && renderDraftButton()}

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/protection-list');
                    }}
                    title="Liste de protection"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Shield className="me-3" />
                    <span className="fw-semibold">Liste de protection</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/messages');
                    }}
                    title="Messages"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <MessageSquare className="me-3" />
                    <span className="fw-semibold">Messages</span>
                  </button>

                  <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/league-history');
                    }}
                    title="Historique"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <HistoryIcon className="me-3" />
                    <span className="fw-semibold">Historique</span>
                  </button>

                  {/* 'Règles' global removed — use ' Règles/Infos Pool' (league specific) */}

                    <button
                    className="btn w-100 d-flex align-items-center justify-content-start py-3 px-3"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/settings');
                    }}
                    title="Paramètres"
                    style={{ background: 'transparent', color: 'white', textAlign: 'left' }}
                  >
                    <Settings className="me-3" />
                    <span className="fw-semibold">Paramètres</span>
                  </button>
                    </>
                  )}
                </>
              )}

              {!username && (
                <Link 
                  className="btn w-100 d-flex align-items-center justify-content-center py-3"
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '15px',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}